/**
 * Simulador de conectores (simulador.html, ADR-15): el banco de trabajo de
 * las conexiones. Sin asesor por medio, hace las mismas peticiones que
 * hará el asesor por el puerto y enseña lo que vuelve en tres vistas
 * (normalizado · en bruto · petición). La receta del concepto se edita y
 * se ejecuta SIN GUARDAR: cuando funciona, se descarga o se copia y se
 * sube al repo por PR.
 *
 * Por qué está hecha así:
 * - La vista recibe el cliente de la API y la cuenta de la fuente por
 *   props: no importa MSAL ni el cliente del Studio, así que se prueba con
 *   dobles y ninguna prueba toca Microsoft.
 * - NADA se persiste (ADR-15): ni el token (estado de React; viaja como
 *   `tokenFuente` en cada llamada), ni el escaneo, ni los resultados, ni
 *   la receta en edición. Ni localStorage ni ningún otro sitio: cerrar la
 *   pestaña lo olvida todo. Desconectar olvida permiso, escaneo y
 *   resultados (y descarta lo que esté en vuelo), pero no la receta en
 *   edición (como el prototipo).
 * - Dos orígenes: Business Central (cuenta de la organización de demo,
 *   escaneo de entornos y empresas) y el banco de escenarios (`fixture`,
 *   sin credenciales), para que la pantalla funcione sin Business Central.
 *   El banco no está en el prototipo: es un segundo `Conn` en la card de
 *   conexión (divergencia anotada en el README del Taller).
 * - El foco no cae a `<body>` al repintar: cada mando que desaparece lo
 *   devuelve a donde lo deja el prototipo (el siguiente «+», la pestaña
 *   abierta, el mando de la conexión, el editor). Lo general —lo que tenía
 *   el foco dentro del panel del resultado desaparece— lo hace `TabPanel`.
 * - La card de conexión tiene dos estados: entera para ELEGIR origen (o
 *   si hay un problema: sesión caducada, fallo de la ventana) y compacta
 *   con un origen EN USO, para que la lista de conceptos —lo que se usa
 *   todo el rato— suba por encima del pliegue. La compacta no es una pieza
 *   nueva: solo se dejan de pintar filas y glosas, en los mismos huecos,
 *   así que los mandos que quedan conservan su nodo y su foco.
 * - Con el banco, los periodos se anclan a lo que cubre el escenario (la
 *   `cobertura` de `conceptos`), no a hoy. La API vieja no la manda: sin
 *   ella, todo como antes.
 * - Los controles se recuerdan por concepto mientras no cambie el origen
 *   (en estado de React, nada se persiste): volver a un concepto lo
 *   ejecuta con lo último que se pidió de él.
 * - Cobertura (ADR-18): la de cada concepto (`coverage`) manda sobre la
 *   del sobre (`cobertura`, la API anterior). Si ninguna dice nada (una
 *   fuente viva) y un resultado `period_not_covered` enseña lo que sí
 *   cubre, «pedir lo que sí cubre» la aprende para ese concepto hasta que
 *   cambie el origen: así los periodos ofrecidos dicen lo mismo que se
 *   pidió. Lo cubierto en parte llega `ok` recortado y se avisa.
 * - Composición SOLO con @skein/ui; la retícula, en layout.css
 *   (page-layout del simulador).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ENTORNO_BC, type ConceptoCatalogo, type ConsultaSimulador, type EjecutarRespuesta, type EmpresasRespuesta, type EntornosRespuesta, type OrigenSimulador } from "@skein/contracts";
import { ApiError, type StudioClient } from "@skein/api-client";
import {
  Badge, BadgeGroup, Banner, Button, ButtonLink, Card, CardHeader, CardPad, CodeBlock, Conn, ConnAct, DateField, Dl, Empty, Fainted, Feed, FeedItem, Field, FieldGroup,
  FormActs, H2, Help, LiveNote, Mono, PageHead, Pill, PillGroup, Select, Skeleton, Spinner, Stale, Sub, Tab, TabPanel, Table, Tabs, TagsInput, TdNum, Textarea, TextField, Th, useToast,
} from "@skein/ui";
import { retryEvery30s } from "../../api/retry";
import { ConexionCancelada, type CuentaFuente } from "./cuenta";
import {
  APP_ENTRA, AVISO_FALTA_CONFIG, ESTADO, GRAN, NIVELES, VARIABLE_CLIENT_ID, ajustarDesglose, chipUltimo, clasificarFallo, coberturaDeConcepto, coberturaDeCubierto, codigoConexion, conFilas,
  construirConsulta, controlesCubiertos, controlesIniciales, diaCorto, desgloseCabe, dimEtiqueta, dimensionQueFalta, dimensionesDe, esEditada, etiquetaCubo, etiquetaEntorno, etiquetaOrigenBC,
  etiquetaPeriodoPuerto, etiquetaRango, falloFrescura, fechaDato, formatoValor, horaCorta, isoLocal, leerDimension, monedaValida, pedidoDentro, periodoElegido, porqueForma, periodos, plantilla, listaGran,
  porDefecto, porqueDesglose, porqueSinPeriodo, frasesNoCubierto, rangoPedido, recorte, rutaReceta, tramoEtiqueta, unidadTexto, type Cobertura, type Controles, type Estado, type Granularidad,
} from "./logica";

export type SimuladorApi = StudioClient["simulador"];

export interface SimuladorProps {
  api: SimuladorApi;
  cuenta: CuentaFuente;
  /** Conmutador dev `?estado=` (vacio · cargando · error), como las hermanas. */
  estado?: string | undefined;
  /** «Hoy» para contar los periodos cerrados (los tests lo fijan). */
  hoy?: Date | undefined;
}

type Bc =
  | { fase: "desconectado" }
  | { fase: "conectando" }
  | { fase: "conectado"; token: string; cuenta: string }
  | { fase: "caducada"; cuenta: string };
type Fuente = "ninguna" | "business-central" | "fixture";
type Vista = "norm" | "raw" | "req";
interface Ultimo { status: Estado; filas: number; a: string }
/** Lo que volvió, con la consulta que lo pidió: el recorte y «pedir lo que sí cubre» comparan con ella. */
interface Resultado { r: EjecutarRespuesta; concepto: string; dims: string[]; consulta: ConsultaSimulador }

const PBI_PORQUE = "Power BI llega en la v2, la semana del 19 de octubre, con su escaneo (áreas de trabajo, modelos, tablas y medidas) y recetas DAX sobre el modelo de demo.";
const FUENTE_NOMBRE: Readonly<Record<string, string>> = { "business-central": "Business Central", fixture: "el banco de escenarios", "power-bi": "Power BI" };
const VISTAS: ReadonlyArray<readonly [Vista, string]> = [["norm", "Normalizado"], ["raw", "En bruto"], ["req", "Petición"]];
const CADUCADA_PORQUE = "La sesión ha caducado: vuelve a conectar primero";
const AYUDA_FIJO = "La tabla del escenario es de periodo fijo: solo se puede pedir entera, sin desglose.";
const mensajeDe = (e: unknown): string => (e instanceof Error ? e.message : String(e));
/** Lo que se cuenta de un fallo de red con el Studio: el mensaje de la API, o que la petición no salió. */
const mensajeRed = (e: unknown): string => (e instanceof ApiError ? e.message : "No llego al Studio: la petición no ha salido.");
const enBody = (): boolean => document.activeElement === null || document.activeElement === document.body;

/* ── piezas pequeñas ──────────────────────────────────────────────────── */

const ANCHOS = ["sim-skel-w40", "", "sim-skel-w80", "sim-skel-w60", ""];
/** Esqueleto: n líneas (anchos del prototipo), un bloque opcional y líneas tras él. */
function Lineas({ n, bloque = false, tras = 0 }: { n: number; bloque?: boolean; tras?: number }) {
  const linea = (i: number) => <Skeleton key={`l${i}`} className={`sim-skel-l ${ANCHOS[i % ANCHOS.length] ?? ""}`.trim()} />;
  return (
    <div className="sim-skel" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => linea(i))}
      {bloque && <Skeleton className="sim-skel-block" />}
      {Array.from({ length: tras }, (_, i) => linea(n + i + 1))}
    </div>
  );
}

/** Celda de una dimensión: el tramo con su etiqueta, la cuenta en mono, el vacío en «—». */
function ValorDimension({ dim, v }: { dim: string; v: string | undefined }) {
  if (v === undefined || v === "") return <Fainted>—</Fainted>;
  const tramo = dim === "bucket" ? tramoEtiqueta(v) : null;
  if (tramo !== null) return <>{tramo}<Sub inline>{v}</Sub></>;
  return dim === "account" ? <Mono>{v}</Mono> : <>{v}</>;
}

/** La tabla de quién arregla qué (brief): las tres vistas dicen dónde está el fallo. */
function NoCuadra() {
  const filas: ReadonlyArray<readonly [string, string, string]> = [
    ["La cifra normalizada no coincide, pero la respuesta en bruto es correcta", "En la receta: traduce mal (signo, periodo, filtro)", "Enric, en la receta"],
    ["La petición ya sale mal planteada", "En la receta: pide mal", "Enric, en la receta"],
    ["Todo cuadra con la fuente, pero no con la hoja de lo esperado", "En lo esperado", "Enric, en la hoja"],
    ["La receta no puede expresar lo que hace falta, o el adaptador falla", "En el código", "Martí; Enric le pasa las tres vistas"],
    ["La fuente no tiene el dato (CRONUS sin presupuesto)", "No es un fallo: «concepto no disponible»", "Enric enriquece la demo o lo anota"],
    ["La definición del concepto es ambigua («ventas», ¿netas de qué?)", "En el catálogo", "Los dos"],
  ];
  return (
    <Card flush aria-label="Cuando algo no cuadra">
      <CardHeader title="Cuando algo no cuadra" hint="las tres vistas dicen dónde está el fallo" />
      <CardPad>
        <Table dense>
          <thead><tr><Th scope="col">Qué se ve</Th><Th scope="col">Dónde está el fallo</Th><Th scope="col">Quién lo arregla</Th></tr></thead>
          <tbody>{filas.map(([a, b, c]) => <tr key={a}><td>{a}</td><td>{b}</td><td>{c}</td></tr>)}</tbody>
        </Table>
      </CardPad>
    </Card>
  );
}

/* ── la pantalla ──────────────────────────────────────────────────────── */

export function Simulador({ api, cuenta, estado, hoy: hoyFijo }: SimuladorProps) {
  const say = useToast();
  const hoy = useMemo(() => hoyFijo ?? new Date(), [hoyFijo]);
  const forced = estado === "vacio" || estado === "cargando" || estado === "error" ? estado : null;

  const catalogo = useQuery({ queryKey: ["simulador", "catalogo"], queryFn: () => api.catalogo(), ...retryEvery30s });
  const escenarios = useQuery({ queryKey: ["simulador", "escenarios"], queryFn: () => api.escenarios(), ...retryEvery30s });

  // conexión y escaneo (fuera del puerto: configuración del adaptador)
  const [bc, setBc] = useState<Bc>({ fase: "desconectado" });
  const [renovando, setRenovando] = useState(false);
  const [fuente, setFuente] = useState<Fuente>("ninguna");
  /** El fallo de la ventana de Microsoft: el mensaje es uno, en español; el código técnico va aparte. */
  const [falloConexion, setFalloConexion] = useState<{ codigo: string | null } | null>(null);
  const [entornos, setEntornos] = useState<EntornosRespuesta | null>(null);
  const [entorno, setEntorno] = useState("");
  const [manuales, setManuales] = useState<string[]>([]);
  const [manualTexto, setManualTexto] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<EmpresasRespuesta | null>(null);
  const [empresaId, setEmpresaId] = useState("");
  const [escaneando, setEscaneando] = useState(false);
  const [falloEscaneo, setFalloEscaneo] = useState<string | null>(null);
  const [escaneadoA, setEscaneadoA] = useState<string | null>(null);
  const [escenario, setEscenario] = useState("");

  // concepto, consulta y resultado
  const [sel, setSel] = useState<string | null>(null);
  const [ctl, setCtl] = useState<Controles | null>(null);
  /** Los últimos controles de cada concepto, mientras no cambie el origen (solo en memoria). */
  const [recordados, setRecordados] = useState<Record<string, Controles>>({});
  /** Lo que un `period_not_covered` enseñó que cubre el origen, por concepto, cuando el origen no lo declara (solo en memoria). */
  const [aprendidas, setAprendidas] = useState<Record<string, Cobertura>>({});
  const [dimTexto, setDimTexto] = useState("");
  const [dimError, setDimError] = useState<string | null>(null);
  const [res, setRes] = useState<Resultado | null>(null);
  const [ultimos, setUltimos] = useState<Record<string, Ultimo>>({});
  const [busy, setBusy] = useState(false);
  const [falloEjecutar, setFalloEjecutar] = useState<{ msg: string | null; reintentar: boolean } | null>(null);
  const [errConsulta, setErrConsulta] = useState<string | null>(null);
  const [tab, setTab] = useState<Vista>("norm");
  const [anuncio, setAnuncio] = useState("");
  /** Sube con cada ejecución, cada cambio de origen y al desconectar: lo que vuelve de una anterior se descarta. */
  const runId = useRef(0);
  /** Sube al desconectar: escaneos, empresas y permisos en vuelo de la sesión anterior se descartan. */
  const sesionId = useRef(0);
  const renovandoYa = useRef(false);

  // receta en edición: por tipo de origen y concepto, solo en memoria
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [derivadas, setDerivadas] = useState<Record<string, string>>({});
  const [errReceta, setErrReceta] = useState<string | null>(null);

  // foco: los mandos a los que vuelve cuando el que lo tenía desaparece
  const bcActRef = useRef<HTMLButtonElement>(null);
  const bancoActRef = useRef<HTMLButtonElement>(null);
  const recetaRef = useRef<HTMLTextAreaElement>(null);
  const plantillaRef = useRef<HTMLButtonElement>(null);
  const addRowRef = useRef<HTMLDivElement>(null);
  const rescanRef = useRef<HTMLButtonElement>(null);
  const probarRef = useRef<HTMLButtonElement>(null);
  /** Las dos columnas: si están apiladas, elegir un concepto baja hasta la consulta. */
  const izquierdaRef = useRef<HTMLDivElement>(null);
  const derechaRef = useRef<HTMLElement>(null);
  const pestanas = useRef<Partial<Record<Vista, HTMLButtonElement | null>>>({});
  /** Qué enfocar tras el próximo repintado (lo deja el manejador que hace desaparecer su mando). */
  const foco = useRef<(() => void) | null>(null);
  /** Qué enfocar al acabar el escaneo: el bloque entero se cambia por el esqueleto mientras dura. */
  const trasEscanear = useRef<(() => HTMLElement | null) | null>(null);
  useLayoutEffect(() => {
    const f = foco.current;
    if (f !== null) { foco.current = null; f(); }
    const t = trasEscanear.current;
    if (t !== null && !escaneando) {
      trasEscanear.current = null;
      if (enBody()) (t() ?? rescanRef.current)?.focus();
    }
  });
  const enfocarPestana = (v: Vista) => { foco.current = () => pestanas.current[v]?.focus(); };

  const empresa = empresas?.empresas.find((e) => e.id === empresaId);
  const origen = useMemo<OrigenSimulador | null>(() => {
    if (forced === "vacio") return null;
    if (fuente === "fixture") return escenario ? { tipo: "fixture", escenario } : null;
    if (fuente === "business-central" && (bc.fase === "conectado" || bc.fase === "caducada") && entorno && empresa) {
      const moneda = monedaValida(empresa.moneda);
      return { tipo: "business-central", entorno, empresaId: empresa.id, empresaNombre: empresa.nombreVisible || empresa.nombre, ...(moneda !== null ? { moneda } : {}) };
    }
    return null;
  }, [forced, fuente, escenario, bc.fase, entorno, empresa]);
  const origenKey = origen === null ? "" : JSON.stringify(origen);
  const tipo = origen?.tipo ?? null;
  const origenEtiqueta = origen === null ? "" : origen.tipo === "fixture"
    ? (escenarios.data?.escenarios.find((e) => e.id === origen.escenario)?.empresa ?? origen.escenario)
    : etiquetaOrigenBC(origen.empresaNombre, origen.entorno);

  const servidos = useQuery({
    queryKey: ["simulador", "conceptos", origenKey],
    queryFn: () => (origen === null ? Promise.reject(new Error("sin origen")) : api.conceptos({ origen })),
    enabled: origen !== null,
    retry: false,
  });

  // cambiar de origen olvida los resultados anteriores (los de otro origen no valen)
  useEffect(() => {
    runId.current += 1;
    setRes(null); setUltimos({}); setBusy(false); setFalloEjecutar(null); setErrConsulta(null); setErrReceta(null); setRecordados({}); setAprendidas({});
  }, [origenKey]);

  // el banco en uso sin escenario elegido (se pulsó «Usar el banco» antes de que llegara la lista): el primero, en cuanto llegue
  const primerEscenario = escenarios.data?.escenarios[0]?.id;
  useEffect(() => {
    if (fuente === "fixture" && escenario === "" && primerEscenario !== undefined) setEscenario(primerEscenario);
  }, [fuente, escenario, primerEscenario]);

  const conceptos: ConceptoCatalogo[] = catalogo.data?.conceptos ?? [];
  const c = conceptos.find((x) => x.id === sel);
  const dispDe = (id: string) => servidos.data?.conceptos.find((x) => x.concept === id);
  const disp = sel === null ? undefined : dispDe(sel);
  /** Lo que cubre el origen para un concepto: la del concepto, la del sobre (API anterior) o, si ninguna, la aprendida de un `period_not_covered`. */
  const coberturaDe = (id: string): Cobertura | undefined => coberturaDeConcepto(dispDe(id), servidos.data?.cobertura?.[id]) ?? aprendidas[id];
  /** La conexión no pudo decir qué sirve (ADR-19): no es lo mismo que una lista vacía. */
  const falloServidos = servidos.data?.fallo;
  const cob = sel === null ? undefined : coberturaDe(sel);
  const tieneReceta = (cc: ConceptoCatalogo): boolean =>
    servidos.data ? servidos.data.conceptos.some((x) => x.concept === cc.id) : tipo === "business-central" && cc.recetas["business-central"] !== null;

  // la receta del repo (solo Business Central: las de fixture se derivan del escenario)
  const claveReceta = tipo !== null && sel !== null ? `${tipo}/${sel}` : null;
  const recetaRepo = useQuery({
    queryKey: ["simulador", "receta", tipo, sel],
    queryFn: () => api.receta("business-central", sel ?? ""),
    enabled: tipo === "business-central" && c !== undefined && c.recetas["business-central"] !== null,
    retry: false,
  });
  const base: string | null = tipo === "business-central" ? (recetaRepo.data?.texto ?? null) : claveReceta !== null ? (derivadas[claveReceta] ?? null) : null;
  const texto = claveReceta !== null ? textos[claveReceta] : undefined;
  const editada = esEditada(texto, base);
  const textoVisible = texto ?? base ?? "";
  const oferta = c ? dimensionesDe(c, disp, editada) : [];

  const red = forced === "error" || catalogo.isError;
  const cargando = forced === "cargando" || (catalogo.isPending && forced === null);
  const sinOrigen = origen === null;

  /* ── conexión ── */

  const caducar = () => setBc((b) => (b.fase === "conectado" ? { fase: "caducada", cuenta: b.cuenta } : b));

  /**
   * Las empresas de un entorno. Si el entorno CAMBIA, las del anterior se
   * olvidan antes de pedir: si no, durante la espera el origen sería el
   * entorno nuevo con una empresa del viejo, que no existe.
   */
  async function cargarEmpresas(token: string, nombre: string, sesion: number): Promise<void> {
    if (nombre !== entorno) { setEmpresas(null); setEmpresaId(""); }
    setEntorno(nombre);
    const r = await api.empresas({ tokenFuente: token, entorno: nombre });
    if (sesion !== sesionId.current) return;
    setEmpresas(r);
    if (r.fallo?.estado === "connection_expired") caducar();
    setEmpresaId((prev) => (r.empresas.some((e) => e.id === prev) ? prev : (r.empresas[0]?.id ?? "")));
  }

  /** Entornos (con los escritos a mano) y empresas del elegido: `preferido` si está, si no el primero. */
  async function escanear(token: string, manualesNuevos: string[] = manuales, preferido: string = entorno): Promise<void> {
    const sesion = sesionId.current;
    setEscaneando(true); setFalloEscaneo(null);
    try {
      const r = await api.entornos({ tokenFuente: token, ...(manualesNuevos.length > 0 ? { entornosManuales: manualesNuevos } : {}) });
      if (sesion !== sesionId.current) return;
      setEntornos(r);
      if (r.fallo?.estado === "connection_expired") { caducar(); return; }
      const elegido = r.entornos.find((e) => e.nombre === preferido) ?? r.entornos[0];
      if (elegido) await cargarEmpresas(token, elegido.nombre, sesion);
      else { setEntorno(""); setEmpresas(null); setEmpresaId(""); }
      if (sesion !== sesionId.current) return;
      setEscaneadoA(horaCorta(new Date()));
    } catch (e) {
      if (sesion === sesionId.current) setFalloEscaneo(mensajeRed(e));
    } finally {
      if (sesion === sesionId.current) setEscaneando(false);
    }
  }

  async function conectar(): Promise<void> {
    if (!cuenta.configurada || bc.fase === "conectando") return;
    const sesion = sesionId.current;
    setFalloConexion(null);
    setBc({ fase: "conectando" });
    try {
      const s = await cuenta.conectar();
      if (sesion !== sesionId.current) return;
      setBc({ fase: "conectado", token: s.token, cuenta: s.cuenta });
      setFuente("business-central");
      say(`Conectado a Business Central como ${s.cuenta}.`);
      await escanear(s.token);
    } catch (e) {
      if (sesion !== sesionId.current) return;
      setBc({ fase: "desconectado" });
      if (e instanceof ConexionCancelada) say("Conexión cancelada: no se ha dado ningún permiso.");
      else setFalloConexion({ codigo: codigoConexion(e) });
    }
  }

  async function reconectar(): Promise<void> {
    // la misma guarda que conectar: dos clics abrirían dos ventanas (interaction_in_progress)
    if (bc.fase !== "caducada" || renovandoYa.current) return;
    renovandoYa.current = true; setRenovando(true);
    const sesion = sesionId.current;
    const antes = bc;
    setFalloConexion(null);
    try {
      const s = await cuenta.renovar();
      if (sesion !== sesionId.current) return;
      setBc({ fase: "conectado", token: s.token, cuenta: s.cuenta });
      say(`Conectado de nuevo como ${s.cuenta}. Vuelve a ejecutar el concepto.`);
    } catch (e) {
      if (sesion !== sesionId.current) return;
      setBc(antes);
      if (!(e instanceof ConexionCancelada)) setFalloConexion({ codigo: codigoConexion(e) });
    } finally {
      renovandoYa.current = false; setRenovando(false);
    }
  }

  async function desconectar(): Promise<void> {
    // lo que esté en vuelo (escaneo, empresas, ejecución) ya no es de nadie
    sesionId.current += 1; runId.current += 1;
    setBc({ fase: "desconectado" });
    setEntornos(null); setEntorno(""); setEmpresas(null); setEmpresaId(""); setEscaneadoA(null); setFalloEscaneo(null); setEscaneando(false);
    setManuales([]); setManualTexto(""); setManualError(null); setFalloConexion(null);
    if (fuente === "business-central") { setFuente("ninguna"); setSel(null); setCtl(null); }
    // con el banco en uso y la card compacta, la fila de Business Central se desmonta con el foco dentro: al mando del banco
    if (fuente === "fixture") foco.current = () => bancoActRef.current?.focus();
    setRes(null); setUltimos({}); setBusy(false); setFalloEjecutar(null); setErrConsulta(null); setErrReceta(null);
    await cuenta.olvidar().catch(() => undefined);
    say("Desconectado: olvidé el permiso, el escaneo y los resultados.");
  }

  function usarBanco(): void {
    if (fuente === "fixture") {
      setFuente(bc.fase === "conectado" || bc.fase === "caducada" ? "business-central" : "ninguna");
      if (bc.fase === "desconectado") { setSel(null); setCtl(null); }
      return;
    }
    // el escenario lo elige el efecto en cuanto haya lista (también si aún no ha llegado)
    setFuente("fixture");
  }

  function probarManual(): void {
    if (bc.fase !== "conectado") return;
    const v = manualTexto.trim();
    if (!ENTORNO_BC.test(v)) { setManualError("Un entorno se escribe como en Business Central: letras, números, guion y guion bajo («Production», «Sandbox»)."); return; }
    setManualError(null);
    const nuevos = manuales.includes(v) ? manuales : [...manuales, v];
    setManuales(nuevos);
    trasEscanear.current = () => probarRef.current;
    // se prueba EL ESCRITO: el `entorno` de este render es el anterior
    void escanear(bc.token, nuevos, v);
  }

  /* ── ejecutar ── */

  /** `cob`: la cobertura con que leer los controles, si acaba de cambiar (la de este render aún es la vieja). */
  async function ejecutar(conceptoId: string, controles: Controles, receta?: string, cob?: Cobertura): Promise<void> {
    const cc = conceptos.find((x) => x.id === conceptoId);
    if (origen === null || cc === undefined || red) return;
    const consulta = construirConsulta(cc, controles, hoy, cob ?? coberturaDe(cc.id));
    const id = ++runId.current;
    setBusy(true); setErrReceta(null); setErrConsulta(null); setFalloEjecutar(null);
    setAnuncio(`Ejecutando ${cc.etiqueta} contra ${origenEtiqueta}…`);
    try {
      const r = await api.ejecutar({
        origen, consulta,
        ...(origen.tipo === "business-central" && bc.fase === "conectado" ? { tokenFuente: bc.token } : {}),
        ...(receta !== undefined ? { receta } : {}),
      });
      if (id !== runId.current) return;
      const derivada = origen.tipo === "fixture" && r.receta?.origen === "derivada" ? r.receta.texto : null;
      if (derivada !== null) setDerivadas((d) => ({ ...d, [`fixture/${cc.id}`]: derivada }));
      setRes({ r, concepto: cc.id, dims: [...controles.dimensiones], consulta });
      const n = r.resultado.rows.length;
      setUltimos((u) => ({ ...u, [cc.id]: { status: r.resultado.status, filas: n, a: horaCorta(new Date()) } }));
      if (r.resultado.status === "connection_expired") caducar();
      setAnuncio(`${cc.etiqueta}: ${ESTADO[r.resultado.status].etiqueta}${n > 0 ? `, ${n} ${n === 1 ? "fila" : "filas"}` : ""}${recorte(consulta, r.resultado) !== null ? ", servido en parte" : ""}.`);
    } catch (e) {
      if (id !== runId.current) return;
      // cada fallo, junto al mando que lo causó: la receta, la consulta o el resultado
      const f = clasificarFallo(e);
      if (f.donde === "resultado") { setFalloEjecutar({ msg: f.msg, reintentar: f.reintentar }); setAnuncio(`No se ha podido ejecutar ${cc.etiqueta}.`); }
      else if (f.donde === "receta") { setErrReceta(f.msg); setAnuncio(`La receta no vale: ${f.msg}`); }
      else { setErrConsulta(f.msg); setAnuncio(`La consulta no vale: ${f.msg}`); }
    } finally {
      if (id === runId.current) setBusy(false);
    }
  }

  /** Los controles del concepto: en pantalla y recordados para cuando se vuelva a él. */
  const ponerCtl = (id: string, nuevos: Controles) => { setCtl(nuevos); setRecordados((r) => ({ ...r, [id]: nuevos })); };

  /**
   * Un clic en un concepto lo ejecuta: con lo último que se pidió de él, o
   * con su consulta por defecto. A una columna, además, baja hasta la
   * consulta (el resultado quedaba fuera de la vista); el foco no se mueve.
   */
  function elegir(id: string): void {
    const cc = conceptos.find((x) => x.id === id);
    if (cc === undefined) return;
    const nuevos = recordados[id] ?? controlesIniciales(cc, dispDe(id)?.dimensions ?? [], hoy, coberturaDe(id));
    setSel(id); ponerCtl(id, nuevos); setDimTexto(""); setDimError(null); setTab("norm");
    const izq = izquierdaRef.current, der = derechaRef.current;
    // apiladas = la derecha empieza por debajo de la izquierda (a dos columnas, a la misma altura)
    if (izq !== null && der !== null && der.getBoundingClientRect().top > izq.getBoundingClientRect().top + 1 && typeof der.scrollIntoView === "function") {
      der.scrollIntoView({ block: "start" });
    }
    void ejecutar(id, nuevos);
  }
  const cambiar = (patch: Partial<Controles>) => { if (ctl !== null && sel !== null) ponerCtl(sel, { ...ctl, ...patch }); };

  function anadirDimension(textoDim: string): void {
    if (c === undefined || ctl === null) return;
    const r = leerDimension(textoDim, c, oferta);
    if ("error" in r) { setDimError(r.error); return; }
    setDimError(null); setDimTexto("");
    if (!ctl.dimensiones.includes(r.dim)) cambiar({ dimensiones: [...ctl.dimensiones, r.dim] });
  }

  /* ── receta ── */

  const ponerTexto = (v: string) => {
    if (claveReceta === null) return;
    setErrReceta(null);
    // volver al texto de referencia a mano cuenta como no editada
    setTextos((t) => { const n = { ...t }; if (base !== null && v === base) delete n[claveReceta]; else n[claveReceta] = v; return n; });
  };
  const volverAlRepo = () => {
    if (claveReceta === null) return;
    setTextos((t) => { const n = { ...t }; delete n[claveReceta]; return n; });
    setErrReceta(null);
    // el enlace desaparece: al editor, o (si era la plantilla) a «empezar una desde la plantilla»
    foco.current = () => (recetaRef.current ?? plantillaRef.current)?.focus();
    say(base !== null ? (tipo === "fixture" ? "Vuelves a la receta derivada del escenario." : "Vuelves a la receta del repo.") : "Plantilla descartada.");
  };
  function probarReceta(): void {
    if (sel === null || ctl === null) return;
    setTab("norm");
    void ejecutar(sel, ctl, editada || base === null ? textoVisible : undefined);
  }
  function descargar(): void {
    if (sel === null) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([textoVisible], { type: "text/yaml" }));
    a.download = `${sel}.yaml`;
    a.click();
    URL.revokeObjectURL(a.href);
    say(tipo === "fixture" ? `Descargada ${sel}.yaml.` : `Descargada ${sel}.yaml: súbela a ${rutaReceta("business-central", sel).replace(/[^/]+$/, "")} por PR.`);
  }
  function copiar(): void {
    const mal = () => say("No he podido copiarla: selecciona el texto y cópialo a mano.");
    try { navigator.clipboard.writeText(textoVisible).then(() => say("Receta copiada."), mal); } catch { mal(); }
  }

  /* ── pintar: conexión ── */

  const bcConectada = bc.fase === "conectado" || bc.fase === "caducada";
  /**
   * Compacta = un origen EN USO y ningún problema: el banco, o Business
   * Central conectado (caducada o un fallo de la ventana la abren entera).
   * Quedan las filas activas —la del origen en uso y, con el banco, la de
   * Business Central si sigue conectado, para poder desconectarlo— y el
   * escaneo. Con Business Central conectado, la fila del banco se queda
   * siempre (sin glosa): «Usar el banco» lo deja en espera sin perder
   * permiso ni escaneo. Las glosas, el aviso de configuración y Power BI
   * vuelven al dejar el origen.
   */
  const compacta = falloConexion === null && (fuente === "fixture" || (fuente === "business-central" && bc.fase === "conectado"));
  const filaBC = !compacta || bcConectada;
  const filaBanco = !compacta || fuente === "fixture" || bc.fase === "conectado";
  const glosaBC = bcConectada && !compacta;
  /**
   * Pasar a compacta puede llegar sin clic (conectar o reconectar resuelven
   * más tarde): si el foco estaba en un mando que se deja de pintar (el
   * «Conectar» apagado de Power BI, una glosa…), cae a <body>. Entonces, al
   * mando de la conexión que sigue (como el prototipo).
   */
  const eraCompacta = useRef(compacta);
  useLayoutEffect(() => {
    if (compacta && !eraCompacta.current && enBody()) (fuente === "fixture" ? bancoActRef.current : bcActRef.current)?.focus();
    eraCompacta.current = compacta;
  }, [compacta, fuente]);
  const bcStatus: ReactNode = !cuenta.configurada ? "falta configurar la app de Entra"
    : bc.fase === "desconectado" ? "desconectado"
    : bc.fase === "conectando" ? "esperando el permiso en la ventana de Microsoft…"
    : bc.fase === "caducada" ? (renovando ? "esperando el permiso en la ventana de Microsoft…" : "sesión caducada · hay que volver a conectar")
    : `conectado como ${bc.cuenta}${fuente === "fixture" ? " · en espera: usas el banco" : ""}`;
  // un solo <Button> en este hueco, sea cual sea la fase: así es el MISMO nodo y el foco que se deja en él sobrevive al cambio
  const bcAct = !cuenta.configurada
    ? <Button ref={bcActRef} size="s" aria-disabled="true" aria-describedby="sim-bc-porque" tip={AVISO_FALTA_CONFIG} tipPos="left">Conectar con Microsoft</Button>
    : bc.fase === "conectando" || renovando ? <Button ref={bcActRef} size="s" loading aria-disabled="true" tabIndex={-1}>Conectando…</Button>
    : bc.fase === "conectado" ? <Button ref={bcActRef} size="s" variant="quiet" hov="danger" aria-describedby={glosaBC ? "sim-bc-glosa" : undefined} onClick={() => void desconectar()}>Desconectar</Button>
    : bc.fase === "caducada" ? <Button ref={bcActRef} size="s" aria-describedby={glosaBC ? "sim-bc-glosa" : undefined} onClick={() => void reconectar()}>Volver a conectar</Button>
    : <Button ref={bcActRef} size="s" onClick={() => void conectar()}>Conectar con Microsoft</Button>;
  const nEsc = escenarios.data?.escenarios.length;

  // los huecos son fijos ({cond && …}): una fila que se deja de pintar no mueve a las demás, y sus mandos conservan nodo y foco
  const conexion = (
    <Card flush aria-label="Conexión">
      <CardHeader title="Conexión" hint={!compacta ? "solo fuentes de demo de Skein" : fuente === "fixture" ? "en uso · déjalo para cambiar de origen" : "en uso · el banco sigue a un clic"} />
      <div className="sim-conns">
        {filaBC && (
          <Conn listed lg="BC" name="Business Central" on={bc.fase === "conectado" && fuente === "business-central"} wait={bc.fase === "conectando" || renovando} ghost={!cuenta.configurada}
            status={<span id="sim-bc-estado">{bcStatus}</span>} act={<ConnAct>{bcAct}</ConnAct>} />
        )}
        {!cuenta.configurada && !compacta && (
          <Banner tone="info" className="sim-gap">
            <span id="sim-bc-porque">{AVISO_FALTA_CONFIG} Hace falta la variable <Mono>{VARIABLE_CLIENT_ID}</Mono> con el id de su app de Entra, <Mono>{APP_ENTRA}</Mono>. Mientras tanto, el banco de escenarios funciona sin credenciales.</span>
          </Banner>
        )}
        {falloConexion !== null && (
          <Banner tone="danger" className="sim-gap">
            <b>No se ha podido conectar con Microsoft.</b> Si el navegador bloqueó la ventana, permítela para este sitio y vuelve a intentarlo.
            {falloConexion.codigo !== null && <> <Sub inline>{falloConexion.codigo}</Sub></>}
          </Banner>
        )}
        {/* la glosa describe el mando que hay a la vista: en caducada es «Volver a conectar», no «Desconectar» */}
        {glosaBC && <Help id="sim-bc-glosa">{bc.fase === "caducada"
          ? "Volver a conectar pide el permiso otra vez en la ventana de Microsoft: el simulador no lo guarda. La receta que estés editando se queda."
          : "Desconectar olvida todo: el permiso, el escaneo y los resultados. El simulador no guarda credenciales; al cerrar la pestaña también se pierden. La receta que estés editando se queda hasta entonces."}</Help>}
        {filaBanco && (
          <Conn listed lg="SC" name="Banco de escenarios" on={fuente === "fixture"}
            status={fuente === "fixture" ? "en uso · datos fijos, sin credenciales" : nEsc === undefined ? "datos fijos del banco · sin credenciales" : `${nEsc} ${nEsc === 1 ? "escenario" : "escenarios"} · sin credenciales`}
            act={<ConnAct><Button ref={bancoActRef} size="s" variant={fuente === "fixture" ? "quiet" : undefined} aria-describedby={compacta ? undefined : "sim-banco-glosa"} onClick={usarBanco}>{fuente === "fixture" ? "Dejar el banco" : "Usar el banco"}</Button></ConnAct>} />
        )}
        {!compacta && <Help id="sim-banco-glosa">Los datos fijos del banco de escenarios, sin pasar por ninguna fuente real: la pantalla funciona sin Business Central. Cada escenario dice qué conceptos sirve en la cabecera de sus fuentes.</Help>}
        {!compacta && (
          <Conn listed ghost lg="PBI" name="Power BI" status="v2 · próximamente"
            act={<ConnAct><Button size="s" aria-disabled="true" aria-describedby="sim-pbi-porque" tip="Llega en la v2: áreas de trabajo, modelos y recetas DAX" tipPos="left">Conectar</Button></ConnAct>} />
        )}
        {!compacta && <Help id="sim-pbi-porque">{PBI_PORQUE}</Help>}
      </div>
      {(fuente === "fixture" || (fuente === "business-central" && bcConectada)) && forced !== "vacio" && (
        <>
          <CardHeader title="Escaneo" hint="con qué origen se trabaja" />
          {fuente === "fixture" ? escaneoBanco() : escaneoBC()}
        </>
      )}
    </Card>
  );

  function escaneoBanco(): ReactNode {
    if (escenarios.isPending) return <div className="sim-scan" aria-busy="true"><Lineas n={2} /></div>;
    const lista = escenarios.data?.escenarios ?? [];
    return (
      <div className="sim-scan">
        {escenarios.isError && (
          <Banner tone="danger">
            <b>No llego a la lista de escenarios.</b> {mensajeRed(escenarios.error)} Reintento solo cada 30 s.{" "}
            <Button variant="link" onClick={() => void escenarios.refetch()}>reintentar ahora</Button>
          </Banner>
        )}
        {lista.length === 0
          ? !escenarios.isError && <Empty inline title="El banco no tiene escenarios con fuentes.">Un escenario sirve datos cuando sus fuentes lo declaran en la cabecera (<Mono>sirve:</Mono>).</Empty>
          : (
            <FieldGroup id="sim-escenario" label="Escenario">
              <Select labelledBy="sim-escenario-l" value={escenario} onChange={(v) => { if (v === escenario) return; setEscenario(v); say("Escenario cambiado: los resultados anteriores se olvidan."); }}
                options={lista.map((e) => ({ value: e.id, label: e.nombre, sub: `${e.empresa ?? "empresa sin nombrar"} · ${e.ficheros.length} ${e.ficheros.length === 1 ? "fichero" : "ficheros"}` }))} />
            </FieldGroup>
          )}
      </div>
    );
  }

  function escaneoBC(): ReactNode {
    if (escaneando) return (
      <div className="sim-scan" aria-busy="true">
        <div className="sim-busy"><Spinner /><Help inline role="status">Escaneando entornos y empresas de Business Central…</Help></div>
        <Lineas n={4} />
      </div>
    );
    const lista = entornos?.entornos ?? [];
    const fallo = entornos?.fallo;
    const sinLista = entornos !== null && (!entornos.listado || fallo !== undefined);
    const nEmp = empresas?.empresas.length ?? 0;
    const apagado = bc.fase !== "conectado";
    return (
      <div className="sim-scan">
        {falloEscaneo !== null && <Banner tone="danger"><b>No he podido escanear.</b> {falloEscaneo}</Banner>}
        {sinLista && (
          <>
            <Banner tone="warn">
              <b>Business Central no dio la lista de entornos.</b> {fallo?.mensaje ?? "Escribe el nombre del entorno tal como aparece en Business Central."}
            </Banner>
            <TextField id="sim-entorno-manual" label="Entorno escrito a mano" value={manualTexto} placeholder="Production"
              help="El nombre exacto del entorno en el centro de administración de Business Central."
              {...(manualError !== null ? { error: manualError } : {})}
              onChange={(e) => { setManualTexto(e.target.value); setManualError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") probarManual(); }} />
            <div>
              <Button ref={probarRef} size="s" onClick={probarManual}
                {...(apagado ? { "aria-disabled": true, "aria-describedby": "sim-bc-estado", tip: CADUCADA_PORQUE } : {})}>Probar este entorno</Button>
            </div>
          </>
        )}
        {lista.length > 0 && (
          <FieldGroup id="sim-entorno" label="Entorno">
            <Select id="sim-entorno-btn" labelledBy="sim-entorno-l" value={entorno} stale={apagado}
              {...(apagado ? { tip: CADUCADA_PORQUE, describedBy: "sim-bc-estado" } : {})}
              onChange={(v) => {
                if (v === entorno || bc.fase !== "conectado") return;
                say(`Entorno cambiado a ${v}: los resultados anteriores se olvidan.`);
                const sesion = sesionId.current;
                setEscaneando(true);
                trasEscanear.current = () => document.getElementById("sim-entorno-btn");
                cargarEmpresas(bc.token, v, sesion)
                  .catch((e: unknown) => { if (sesion === sesionId.current) setFalloEscaneo(mensajeRed(e)); })
                  .finally(() => { if (sesion === sesionId.current) setEscaneando(false); });
              }}
              options={lista.map((e) => ({ value: e.nombre, label: e.nombre, sub: etiquetaEntorno(e) }))} />
          </FieldGroup>
        )}
        {entorno !== "" && (
          empresas !== null && nEmp === 0
            ? <Empty inline title={`Sin empresas en ${entorno}.`}>{empresas.fallo?.mensaje ?? "Business Central no devolvió ninguna empresa en ese entorno."}</Empty>
            : (
              <FieldGroup id="sim-empresa" label="Empresa">
                <Select labelledBy="sim-empresa-l" value={empresaId} stale={empresas === null}
                  onChange={(v) => { if (v === empresaId) return; setEmpresaId(v); say(`Origen cambiado a ${empresas?.empresas.find((e) => e.id === v)?.nombreVisible ?? v}: los resultados anteriores se olvidan.`); }}
                  options={(empresas?.empresas ?? []).map((e) => ({ value: e.id, label: e.nombreVisible || e.nombre, sub: `${e.nombre}${e.moneda ? ` · ${e.moneda}` : ""}` }))} />
              </FieldGroup>
            )
        )}
        <div className="sim-scan-foot">
          <Help inline>{escaneadoA !== null ? `Escaneado hoy a las ${escaneadoA} · ${lista.length} ${lista.length === 1 ? "entorno" : "entornos"}${entorno ? ` · ${nEmp} ${nEmp === 1 ? "empresa" : "empresas"} en ${entorno}` : ""}` : "Sin escanear todavía."}</Help>
          <Button ref={rescanRef} size="s" variant="quiet"
            {...(apagado ? { "aria-disabled": true, "aria-describedby": "sim-bc-estado", tip: CADUCADA_PORQUE, tipPos: "left" as const } : {})}
            onClick={() => { if (bc.fase === "conectado") { trasEscanear.current = () => rescanRef.current; void escanear(bc.token); } }}>Escanear de nuevo</Button>
        </div>
      </div>
    );
  }

  /* ── pintar: conceptos ── */

  const todosDraft = conceptos.length > 0 && conceptos.every((x) => x.estado === "draft");
  const fuenteNombre = tipo !== null ? (FUENTE_NOMBRE[tipo] ?? tipo) : "";
  // con una fuente en uso pero sin origen elegido, el vacío no dice «sin conexión»: dice qué falta elegir
  const conFuenteSinOrigen = forced !== "vacio" && (fuente === "fixture" || (fuente === "business-central" && bcConectada));
  const listaConceptos = (
    <Card flush aria-label="Conceptos">
      <CardHeader title="Conceptos" hint={catalogo.data ? `catálogo v${catalogo.data.versionCatalogo} · ${conceptos.length}${todosDraft ? ", todos en draft" : ""} · un clic ejecuta` : "el catálogo del puerto"} />
      {cargando ? <CardPad aria-busy="true"><Lineas n={5} /></CardPad>
        : catalogo.data === undefined ? <CardPad><Empty inline title="Sin catálogo.">No llego al catálogo de conceptos: sin él no hay nada que pedir.</Empty></CardPad>
        : sinOrigen ? (escaneando || bc.fase === "conectando" || (fuente === "fixture" && escenarios.isPending) ? <CardPad aria-busy="true"><Lineas n={5} /></CardPad>
          : conFuenteSinOrigen
            ? <CardPad><Empty inline title="Sin origen, sin catálogo servible.">{`La lista es el catálogo; cada concepto dice si tiene receta para el origen elegido. Elige ${fuente === "fixture" ? "un escenario del banco" : "entorno y empresa"} para verla.`}</Empty></CardPad>
            : <CardPad><Empty inline title="Sin conexión, sin catálogo servible.">La lista es el catálogo; cada concepto dice si tiene receta para el origen conectado. Conecta Business Central o usa el banco de escenarios para verla.</Empty></CardPad>)
        // mientras llega lo que sirve el origen, esqueleto: la lista con todo «sin receta» mentiría
        // «reintentar ahora» tras un fallo también es cargar: el esqueleto da la señal y, al volver, el aviso se remonta y se anuncia otra vez
        : servidos.isPending || (servidos.isFetching && falloServidos !== undefined) ? <CardPad aria-busy="true"><Lineas n={5} /></CardPad>
        // la conexión no pudo decir qué sirve: la lista entera «sin receta» también mentiría (una vacía SIN fallo sí es «no sirve ninguno»)
        : falloServidos !== undefined ? (
          <CardPad>
            <Banner tone="danger">
              <b>No se pudo saber qué sirve el origen.</b> {falloServidos.mensaje} Sin eso, la lista diría «sin receta» en todos los conceptos, y no sería verdad.{" "}
              <Button variant="link" onClick={() => void servidos.refetch()}>reintentar ahora</Button>
            </Banner>
          </CardPad>
        )
        : (
          <>
            {servidos.isError && <CardPad><Banner tone="danger"><b>El origen no se deja leer.</b> {mensajeDe(servidos.error)}</Banner></CardPad>}
            {NIVELES.map((nivel) => {
              const cs = conceptos.filter((x) => x.nivel === nivel.id);
              if (cs.length === 0) return null;
              const nr = cs.filter(tieneReceta).length;
              return (
                <div key={nivel.id}>
                  <CardHeader title={nivel.titulo} hint={`${nr} de ${cs.length} con receta`} />
                  <Feed full>
                    {cs.map((x) => {
                      const rec = tieneReceta(x);
                      const ed = x.id === sel ? editada : textos[`${tipo ?? ""}/${x.id}`] !== undefined;
                      const u = ultimos[x.id];
                      const chip = busy && sel === x.id ? { texto: "ejecutando…", tono: "accent" as const } : u ? chipUltimo(u.status, u.filas) : null;
                      const version = dispDe(x.id)?.mapping.version ?? x.recetas["business-central"]?.version;
                      return (
                        <FeedItem key={x.id} t={u?.a ?? "—"} sel={sel === x.id} onClick={() => elegir(x.id)}>
                          <b>{x.etiqueta}</b>
                          <Sub inline>{x.id}{rec || ed ? "" : " · sin receta"}</Sub>
                          <BadgeGroup>
                            {chip !== null && <Badge tone={chip.tono}>{chip.texto}</Badge>}
                            <Badge>{`v${x.version} · ${x.estado}`}</Badge>
                            {(rec || ed) && <Badge>{ed ? "receta editada" : version !== undefined ? `receta v${version}` : "receta"}</Badge>}
                          </BadgeGroup>
                        </FeedItem>
                      );
                    })}
                  </Feed>
                </div>
              );
            })}
          </>
        )}
    </Card>
  );

  /* ── pintar: consulta ── */

  function consulta(): ReactNode {
    if (c === undefined || ctl === null) return (
      <Card flush aria-label="Consulta">
        <CardHeader title="Consulta" hint="elige un concepto de la lista" />
        <CardPad><Empty inline title="Sin concepto elegido.">Elige uno de la lista: un clic lo ejecuta con su consulta por defecto, y aquí podrás cambiar periodo, desglose y dimensiones.</Empty></CardPad>
      </Card>
    );
    const flujo = c.temporal === "flow";
    // con cobertura fija (la tabla solo se pide entera) no hay tipo de periodo ni desglose que elegir
    const fijo = cob?.fija === true;
    const ops = periodos(ctl.tipo, hoy, cob);
    /** Los desgloses que el concepto admite pero no caben en el periodo (D6): van apagados y se nombran en la glosa. */
    const gruesas = c.granularidades.filter((g) => !desgloseCabe(g, ctl.tipo));
    /** Los tipos de periodo sin ninguna opción dentro de lo que cubre el escenario: apagados, y nombrados en su glosa. */
    const sinCubrir = (["month", "quarter", "year"] as const).filter((g) => porqueSinPeriodo(g, hoy, cob) !== null);
    const p = periodoElegido(ctl, hoy, cob);
    const resto = oferta.filter((o) => !ctl.dimensiones.includes(o.dim));
    const ofrecidas = resto.filter((o) => o.ofrecida);
    /** «+ X»: el botón desaparece; el foco pasa al siguiente «+» que se pueda pulsar, o al campo (como el prototipo). */
    const anadirDesdeBoton = (dim: string) => {
      const idx = ofrecidas.findIndex((o) => o.dim === dim);
      setDimError(null);
      cambiar({ dimensiones: [...ctl.dimensiones, dim] });
      foco.current = () => {
        const quedan = [...(addRowRef.current?.querySelectorAll<HTMLButtonElement>('button:not([aria-disabled="true"])') ?? [])];
        (quedan[Math.min(idx, quedan.length - 1)] ?? document.getElementById("sim-dims"))?.focus();
      };
    };
    return (
      <Card flush aria-label="Consulta">
        <CardHeader title="Consulta" hint={`${c.etiqueta} · ${flujo ? "flujo de un periodo" : "saldo a una fecha"} · ${c.unidad === "currency" ? "importe" : c.unidad === "percentage" ? "porcentaje" : c.unidad === "days" ? "días" : "recuento"}`} />
        <div className="sim-qrow">
          {flujo && !fijo && (
            <FieldGroup id="sim-tipo" label="Periodo">
              <PillGroup label="Periodo" describedBy={sinCubrir.length > 0 ? "sim-tipo-why" : undefined}>
                {(["month", "quarter", "year"] as const).map((g) => {
                  // un tipo sin ninguna opción dentro de lo que cubre el escenario va apagado, con porqué
                  const porque = porqueSinPeriodo(g, hoy, cob);
                  return (
                    <Pill key={g} pressed={ctl.tipo === g} stale={porque !== null} tip={porque ?? undefined}
                      onClick={() => cambiar({ tipo: g, periodo: porDefecto(periodos(g, hoy, cob))?.valor ?? "", granularidad: ajustarDesglose(ctl.granularidad, g, c.granularidades) })}>{GRAN[g]}</Pill>
                  );
                })}
              </PillGroup>
              {/* el apagado sale del teclado (Pill stale): el porqué ha de verse, no solo vivir en el globo */}
              {sinCubrir.length > 0 && <Help inline id="sim-tipo-why" className="sim-gran-why">{`${listaGran(sinCubrir)}: el escenario no cubre ninguno entero.`}</Help>}
            </FieldGroup>
          )}
          {flujo && (
            <FieldGroup id="sim-periodo" label="Periodo concreto" {...(fijo ? { help: AYUDA_FIJO } : {})}>
              <Select labelledBy="sim-periodo-l" describedBy={fijo ? "sim-periodo-h" : undefined} value={p?.valor ?? ""} onChange={(v) => cambiar({ periodo: v })}
                options={ops.map((o) => ({ value: o.valor, label: o.etiqueta, sub: o.sub }))} />
            </FieldGroup>
          )}
          {!flujo && (
            <FieldGroup id="sim-fecha" label="Fecha del saldo" className="sim-datefield">
              <DateField id="sim-fecha-in" labelledBy="sim-fecha-l" value={ctl.fecha} onChange={(d) => cambiar({ fecha: d })}
                today={isoLocal(hoy)} max={isoLocal(hoy)} maxReason="aún no ha llegado" calendarLabel="Elige la fecha del saldo" />
            </FieldGroup>
          )}
          {flujo && !fijo && c.granularidades.length > 0 && (
            <FieldGroup id="sim-gran" label="Desglose en el tiempo">
              <PillGroup label="Desglose en el tiempo" describedBy={gruesas.length > 0 ? "sim-gran-why" : undefined}>
                <Pill pressed={ctl.granularidad === null} onClick={() => cambiar({ granularidad: null })}>Sin desglose</Pill>
                {/* nunca más grueso que el periodo: lo que no cabe se ve apagado, con porqué */}
                {c.granularidades.map((g: Granularidad) => {
                  const cabe = desgloseCabe(g, ctl.tipo);
                  return <Pill key={g} pressed={ctl.granularidad === g} stale={!cabe} tip={cabe ? undefined : porqueDesglose(g, ctl.tipo)} onClick={() => cambiar({ granularidad: g })}>{GRAN[g]}</Pill>;
                })}
              </PillGroup>
              {/* la glosa visible de lo apagado (el globo de cada píldora se queda); Pill no admite aria-describedby: se enlaza al grupo */}
              {gruesas.length > 0 && <Help inline id="sim-gran-why" className="sim-gran-why">{`${listaGran(gruesas)}: ${gruesas.length === 1 ? "más grueso" : "más gruesos"} que el periodo.`}</Help>}
            </FieldGroup>
          )}
          <Field id="sim-dims" label="Dimensiones" className="sim-qdims" errorAlert {...(dimError !== null ? { error: dimError } : {})}>
            <TagsInput id="sim-dims" value={dimTexto} onChange={(v) => { setDimTexto(v); setDimError(null); }}
              tags={ctl.dimensiones.map((d) => ({ value: d, label: dimEtiqueta(d), removeLabel: `Quitar ${dimEtiqueta(d)}` }))}
              onAdd={anadirDimension} onRemove={(d) => cambiar({ dimensiones: ctl.dimensiones.filter((x) => x !== d) })}
              placeholder={ctl.dimensiones.length > 0 ? "Añade una dimensión…" : "Sin dimensiones: una sola cifra"}
              describedBy={`sim-dims-add${dimError !== null ? " sim-dims-e" : ""}`} invalid={dimError !== null} />
            <div className="sim-addrow" id="sim-dims-add" ref={addRowRef}>
              {c.dimensiones.length === 0 ? <Help inline>El concepto no admite dimensiones.</Help>
                : resto.length === 0 ? <Help inline>Todas las que admite el concepto.</Help>
                : <>
                    <Help inline>Añadir:</Help>
                    {resto.map((o) => (
                      <span key={o.dim} className="sim-addopt">
                        <Button variant="link" stale={!o.ofrecida} tip={o.ofrecida ? undefined : "la receta no la ofrece"}
                          aria-describedby={o.ofrecida ? undefined : `sim-nooff-${o.dim}`}
                          onClick={() => { if (o.ofrecida) anadirDesdeBoton(o.dim); }}>+ {dimEtiqueta(o.dim)}</Button>
                        {!o.ofrecida && <Sub inline id={`sim-nooff-${o.dim}`}>la receta no la ofrece</Sub>}
                      </span>
                    ))}
                  </>}
            </div>
          </Field>
        </div>
        <div className="sim-qacts">
          <Button variant="primary" loading={busy} disabled={red} onClick={() => void ejecutar(c.id, ctl)}>Ejecutar</Button>
          <Help inline>
            {`Contra ${origenEtiqueta}, con la receta ${tipo === "fixture" ? "derivada del escenario" : "del repo"}`}
            {/* sin saber qué sirve el origen no se afirma que no haya receta */}
            {editada ? ". La editada se prueba abajo, con «Ejecutar sin guardar»." : tieneReceta(c) || falloServidos !== undefined ? "." : ": no hay, así que dirá «concepto no disponible»."}
          </Help>
        </div>
        {errConsulta !== null && <CardPad><Banner tone="warn"><b>La consulta no vale.</b> {errConsulta}</Banner></CardPad>}
      </Card>
    );
  }

  /* ── pintar: resultado ── */

  // sin role: quien anuncia el resultado es la región viva (un solo anuncio, como el prototipo)
  function bannerDe({ r, dims, consulta: pedida }: Resultado): ReactNode {
    const rr = r.resultado;
    const st = rr.status;
    const enBruto = <Button variant="link" onClick={() => { setTab("raw"); enfocarPestana("raw"); }}>en bruto</Button>;
    if (st === "stale") {
      const dd = rr.dataDate ?? r.frescura.dataDate;
      return <Banner tone="warn" live={false} className="sim-gap"><b>Dato viejo.</b> Las filas son de lo que había {dd !== null ? `el ${fechaDato(dd)}` : "en una fecha que el adaptador no supo decir"}: más antiguas que el umbral de frescura. El asesor las daría diciendo la fecha.</Banner>;
    }
    // «no disponible» es solo «sin receta / no servido» (ADR-18): lo que cae fuera de lo que cubre el origen es `period_not_covered`
    if (st === "unsupported_concept") return <Banner tone="info" live={false} className="sim-gap"><b>Concepto no disponible.</b> No es un fallo: sin receta para {fuenteNombre} no se improvisa, y no se ha pedido nada a la fuente. {tipo === "business-central" ? "Escríbela abajo, pruébala sin guardar y súbela por PR." : "El escenario no lo sirve en la cabecera de sus fuentes."}</Banner>;
    if (st === "missing_dimension") {
      // con la receta editada no se sabe qué ofrece (el YAML lo lee la API): no se adivina cuál falta, lo dice el contrato
      const deEditada = r.receta?.editada === true;
      const falta = deEditada ? null : dimensionQueFalta(dims, dispDe(rr.concept));
      const quitar = () => {
        if (ctl === null || sel === null) return;
        const nuevos = { ...ctl, dimensiones: falta === null ? [] : ctl.dimensiones.filter((d) => d !== falta) };
        ponerCtl(sel, nuevos);
        enfocarPestana(tab);
        void ejecutar(sel, nuevos, editada ? textoVisible : undefined);
      };
      return (
        <Banner tone="warn" live={false} className="sim-gap">
          <b>Falta una dimensión.</b>{" "}
          {deEditada
            ? <>La receta editada no sabe desglosar todo lo pedido{rr.message !== undefined ? <> («{rr.message}»)</> : null}, así que no se ha pedido nada a la fuente.</>
            : <>La receta no sabe desglosar {falta !== null ? `por ${dimEtiqueta(falta)}` : "por alguna de las pedidas"}, así que no se ha pedido nada a la fuente.</>}{" "}
          <Button variant="link" onClick={quitar}>{falta !== null ? `quitar ${dimEtiqueta(falta)} y ejecutar` : "quitar las dimensiones y ejecutar"}</Button>
        </Banner>
      );
    }
    if (st === "period_not_covered") {
      const cub = rr.covered;
      const cc = conceptos.find((x) => x.id === rr.concept);
      const pedido = rangoPedido(pedida);
      const pedidoTxt = pedido === null ? "lo pedido" : pedida.fecha !== undefined ? `el dato a ${diaCorto(pedida.fecha)}` : etiquetaRango(pedido.inicio, pedido.fin);
      // los periodos que se ofrecen salen de la cobertura en uso; si el origen no declara ninguna, de lo que dice este resultado
      const cob = cub === undefined ? undefined : (coberturaDe(rr.concept) ?? coberturaDeCubierto(cub));
      const nuevos = cub !== undefined && cob !== undefined && cc !== undefined && ctl !== null && sel === rr.concept ? controlesCubiertos(cc, ctl, cob, cub.granularities, hoy) : null;
      // si lo que se pediría es lo mismo que ya falló, no se ofrece: daría vueltas
      const repite = nuevos !== null && cc !== undefined && JSON.stringify(construirConsulta(cc, nuevos, hoy, cob)) === JSON.stringify(pedida);
      const pedirCubierto = () => {
        if (nuevos === null || cub === undefined || cob === undefined || sel === null) return;
        if (coberturaDe(sel) === undefined) setAprendidas((a) => ({ ...a, [sel]: cob }));
        ponerCtl(sel, nuevos);
        enfocarPestana(tab);
        void ejecutar(sel, nuevos, editada ? textoVisible : undefined, cob);
      };
      const fr = cub === undefined ? null : frasesNoCubierto(pedida, cub);
      return (
        <Banner tone="warn" live={false} className="sim-gap">
          <b>Periodo no cubierto.</b>{" "}
          {cub === undefined || fr === null
            ? <>El origen no tiene {pedida.fecha !== undefined ? pedidoTxt : `datos de ${pedidoTxt}`}, así que no se ha rellenado nada. El mensaje del contrato dice qué cubre.</>
            : pedidoDentro(pedida, cub)
              // el rango sí está: falla la forma (desglose, meses enteros, tabla entera); el mensaje del contrato va en su fila, no en la prosa
              ? <>El origen {fr.tiene}, pero {porqueForma(pedida, cub)}. No se ha rellenado nada.</>
              : <>El origen no tiene {fr.pedido}: {fr.cubre}. No se ha rellenado nada.</>}
          {nuevos !== null && !repite && <>{" "}<Button variant="link" onClick={pedirCubierto}>pedir lo que sí cubre y ejecutar</Button></>}
        </Banner>
      );
    }
    if (st === "permission_denied") return <Banner tone="danger" live={false} className="sim-gap"><b>Sin permiso.</b> La fuente ha dicho que no a la cuenta conectada. Revisa sus permisos en la organización de demo; la respuesta está {enBruto}.</Banner>;
    if (st === "connection_expired") return (
      <Banner tone="danger" live={false} className="sim-gap">
        <b>Conexión caducada.</b> El permiso de Microsoft ha vencido y el simulador no lo guarda: hay que pedirlo otra vez.{" "}
        {/* el enlace se va al renovar: el foco pasa ANTES al mando de la conexión, que sigue a la vista */}
        {bc.fase === "caducada" && !renovando && <Button variant="link" onClick={() => { bcActRef.current?.focus(); void reconectar(); }}>volver a conectar</Button>}
      </Banner>
    );
    if (st === "source_error") return <Banner tone="danger" live={false} className="sim-gap"><b>Error de la fuente.</b> La fuente falló, no encontró lo que pide la receta o la respuesta no se pudo leer: el mensaje del contrato dice cuál. Si es de la fuente y se repite, pásale a Martí las tres vistas; el cuerpo del error está {enBruto}.</Banner>;
    return null;
  }

  function resultado(): ReactNode {
    const cuerpo = (() => {
      if (busy) return (
        <CardPad>
          <div className="sim-busy sim-gap"><Spinner /><Help inline>{`Ejecutando ${c?.etiqueta ?? "el concepto"} contra ${origenEtiqueta}…`}</Help></div>
          <Lineas n={3} bloque />
        </CardPad>
      );
      if (falloEjecutar !== null) return (
        <CardPad>
          <Banner tone="danger" live={false}>
            <b>No se ha podido ejecutar.</b> {falloEjecutar.msg ?? "No llego al Studio: la petición no ha salido."}{" "}
            {falloEjecutar.reintentar && sel !== null && ctl !== null && <Button variant="link" onClick={() => { enfocarPestana(tab); void ejecutar(sel, ctl); }}>reintentar ahora</Button>}
          </Banner>
        </CardPad>
      );
      if (res === null) return <CardPad><Empty inline title="Sin resultado aún.">{sel !== null ? "Pulsa «Ejecutar» para pedirlo a la fuente." : "Elige un concepto de la lista: un clic lo ejecuta."}</Empty></CardPad>;
      const { r, dims, consulta: pedida } = res;
      const rr = r.resultado;
      const cc = conceptos.find((x) => x.id === rr.concept);
      const est = ESTADO[rr.status];
      // la fecha del dato es la del resultado (el contrato manda); la de frescura, si el resultado no la trae
      const dataDate = rr.dataDate ?? r.frescura.dataDate;
      const frescuraMal = falloFrescura(r.frescura);
      const recortado = recorte(pedida, rr);
      if (tab === "raw") {
        const sinBruto = r.enBruto === null || r.enBruto === undefined;
        return sinBruto
          ? <CardPad><Empty inline title="No hay respuesta.">{rr.status === "unsupported_concept" ? "Sin receta no se pide nada a la fuente." : rr.status === "period_not_covered" ? "El origen no cubre lo pedido: no se ha leído nada." : rr.status === "missing_dimension" ? "Lo que la receta no ofrece no se pide: se degrada antes de la petición." : "No llegó a enviarse nada a la fuente, o la fuente no devolvió cuerpo."}</Empty></CardPad>
          : <CardPad><Help className="sim-gap">Tal cual lo devolvió la fuente.</Help><CodeBlock label="Respuesta en bruto">{typeof r.enBruto === "string" ? r.enBruto : JSON.stringify(r.enBruto, null, 2)}</CodeBlock></CardPad>;
      }
      if (tab === "req") {
        return r.peticion === null
          ? <CardPad><Empty inline title="No se envió nada a la fuente.">{rr.status === "unsupported_concept" ? `No hay receta que ejecutar para ${fuenteNombre}.` : rr.status === "period_not_covered" ? "El origen no cubre lo pedido: no se llegó a pedir." : rr.status === "missing_dimension" ? "La consulta pide una dimensión que la receta no ofrece." : "La petición no se llegó a construir."}</Empty></CardPad>
          : (
            <CardPad>
              <div className="sim-gap"><CodeBlock label="Petición enviada">{`${r.peticion.resumen}\n\n${JSON.stringify(r.peticion.detalle, null, 2)}`}</CodeBlock></div>
              <Dl items={[
                ...(rr.provenance !== null ? [{ dt: "Receta", dd: <><Mono>{rr.provenance.mapping.id}</Mono> · v{rr.provenance.mapping.version}{r.receta?.editada === true ? " · editada, sin guardar" : ""}</> }] : []),
                ...(tipo === "business-central" ? [{ dt: "Credencial", dd: "delegada de la cuenta conectada; se enseña recortada y no se guarda" }] : [{ dt: "Credencial", dd: "ninguna: el banco de escenarios no pide permiso" }]),
              ]} />
            </CardPad>
          );
      }
      const hasPer = rr.rows.some((x) => x.period !== undefined);
      const columnas = dims.length > 0 ? dims : Object.keys(rr.rows[0]?.dimensions ?? {});
      const periodo = rr.period === null ? <>—<Sub inline>no se llegó a servir</Sub></>
        : rr.period.kind === "period" ? <>{`${diaCorto(rr.period.start)} → ${diaCorto(rr.period.end)}`}<Sub inline>{`${rr.period.start} → ${rr.period.end}`}</Sub></>
        : <>{`a ${diaCorto(rr.period.date)}`}<Sub inline>{rr.period.date}</Sub></>;
      const prov = rr.provenance === null ? <>—<Sub inline>sin receta: no hay procedencia</Sub></> : (
        <>
          {`${FUENTE_NOMBRE[rr.provenance.sourceType] ?? rr.provenance.sourceType} · ${rr.provenance.origin.label} · receta v${rr.provenance.mapping.version}`}
          {r.receta?.editada === true && <> <Badge tone="accent">editada · sin guardar</Badge></>}
          <Sub inline>{rr.provenance.file ? `${rr.provenance.mapping.id} · ${rr.provenance.file.path} (${fechaDato(rr.provenance.file.date)})` : rr.provenance.mapping.id}</Sub>
        </>
      );
      return (
        <>
          <CardPad>
            <div className="sim-reshead">
              <H2 look="h3">{cc?.etiqueta ?? rr.concept}</H2>
              <BadgeGroup><Badge tone={est.tono}>{est.etiqueta}</Badge><Badge>{`concepto v${rr.conceptVersion}${cc ? ` · ${cc.estado}` : ""}`}</Badge></BadgeGroup>
              {/* la etiqueta de máquina solo si dice algo que la insignia no dice («ok» saldría dos veces) */}
              {rr.status !== est.etiqueta && <Sub inline>{rr.status}</Sub>}
            </div>
            {bannerDe(res)}
            {/* lo cubierto en parte llega `ok` con el periodo recortado (ADR-18): se dice qué se pidió y qué se sirve */}
            {recortado !== null && (
              <Banner tone="info" live={false} className="sim-gap">
                <b>Servido en parte.</b> Se pidió {recortado.pedido}, pero el origen solo cubre {recortado.servido} de ese periodo: el resultado es de {recortado.servido}, sin rellenar el resto.
              </Banner>
            )}
            <Dl items={[
              { dt: "Periodo servido", dd: periodo },
              ...(rr.covered !== undefined ? [{ dt: "Lo que cubre", dd: <>{etiquetaPeriodoPuerto(rr.covered.period)}<Sub inline>{rr.covered.granularities.length > 0 ? `desglose: ${listaGran(rr.covered.granularities).toLowerCase()}` : "solo entero, sin desglose"}</Sub></> }] : []),
              { dt: "Unidad", dd: unidadTexto(rr.unit, rr.currency, rr.sign) },
              // sin fecha porque la conexión falló al pedirla (ADR-19) no es lo mismo que un adaptador que no la sabe
              { dt: "Dato de", dd: dataDate !== null ? fechaDato(dataDate) : <>—<Sub inline>{frescuraMal ?? "el adaptador no lo supo"}</Sub></> },
              { dt: "Procedencia", dd: prov },
              ...(rr.message !== undefined ? [{ dt: "Mensaje del contrato", dd: <><Mono>{rr.message}</Mono><Sub inline>lo que diría el asesor al degradar (R-4)</Sub></> }] : []),
            ]} />
          </CardPad>
          {!conFilas(rr.status)
            ? <CardPad><Empty inline title="Sin filas.">En este estado el resultado no trae cifras: el asesor dirá el mensaje, nunca rellenará.</Empty></CardPad>
            : rr.rows.length === 0
              ? <CardPad><Empty inline solid title="Sin movimientos.">{`La fuente no devolvió registros de ${origenEtiqueta} ${cc?.temporal === "balance" ? "a esa fecha" : "en ese periodo"}: es un resultado «ok» con cero filas, no un fallo.`}</Empty></CardPad>
              : (
                <CardPad>
                  <Table dense label="Filas del resultado">
                    <thead><tr>{hasPer && <Th scope="col">Periodo</Th>}{columnas.map((d) => <Th key={d} scope="col">{dimEtiqueta(d)}</Th>)}<Th scope="col" num>Valor</Th></tr></thead>
                    <tbody>
                      {rr.rows.map((x, i) => (
                        <tr key={i}>
                          {hasPer && <td>{x.period !== undefined ? <>{etiquetaCubo(x.period)}<Sub inline>{x.period}</Sub></> : <Fainted>—</Fainted>}</td>}
                          {columnas.map((d) => <td key={d}><ValorDimension dim={d} v={x.dimensions[d]} /></td>)}
                          <TdNum>{formatoValor(x.value, rr.unit, rr.currency)}</TdNum>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Help className="sim-res-foot">{`${rr.rows.length} ${rr.rows.length === 1 ? "fila" : "filas"}, en el orden en que las entrega el contrato: por periodo y después por valor de dimensión; las filas a cero no se quitan. Sumas y variaciones no se hacen aquí: las hace Skein (R-8).`}</Help>
                </CardPad>
              )}
        </>
      );
    })();
    return (
      <Card flush aria-label="Resultado" aria-busy={busy || undefined}>
        <CardHeader title="Resultado" hint="lo que recibirá el asesor, y de dónde sale" />
        <Tabs className="sim-tabs" label="Vistas del resultado">
          {VISTAS.map(([v, nombre]) => (
            <Tab key={v} id={`sim-tab-${v}`} controls={`sim-panel-${v}`} selected={tab === v} onClick={() => setTab(v)}
              ref={(el) => { pestanas.current[v] = el; }}>{nombre}</Tab>
          ))}
        </Tabs>
        {VISTAS.map(([v]) => (
          <TabPanel key={v} id={`sim-panel-${v}`} labelledBy={`sim-tab-${v}`} hidden={tab !== v}>{tab === v ? cuerpo : null}</TabPanel>
        ))}
      </Card>
    );
  }

  /* ── pintar: receta ── */

  // las secciones son funciones que devuelven JSX, no componentes: definidas
  // aquí dentro, como componentes se remontarían en cada render y el campo
  // que se está escribiendo perdería el foco
  function receta(): ReactNode {
    return (
      <Card flush aria-label="Receta">
        <CardHeader title="Receta" badge={editada ? <Badge tone="accent">editada · sin guardar</Badge> : undefined} hint="las recetas, libres; los conceptos, acordados" />
        <CardPad className="sim-recipe">{cuerpoReceta()}</CardPad>
      </Card>
    );
  }
  function cuerpoReceta(): ReactNode {
    if (c === undefined || claveReceta === null) return <Empty inline title="Sin concepto elegido.">La receta del concepto que elijas se abre aquí.</Empty>;
    const repoPendiente = tipo === "business-central" && c.recetas["business-central"] !== null && recetaRepo.isPending;
    if (repoPendiente) return <Lineas n={4} />;
    const repoNoLlega = tipo === "business-central" && recetaRepo.isError && !(recetaRepo.error instanceof ApiError && recetaRepo.error.status === 404);
    if (repoNoLlega && texto === undefined) return <Empty inline title="No llego a la receta del repo.">{mensajeRed(recetaRepo.error)} <Button variant="link" onClick={() => void recetaRepo.refetch()}>reintentar ahora</Button></Empty>;
    if (base === null && texto === undefined) {
      if (tipo === "fixture") return <Empty inline title="Receta derivada del escenario.">En el banco la receta sale de la cabecera de cada fuente del escenario. Ejecuta el concepto y aquí aparecerá la que se usó, para editarla y probarla sin guardar.</Empty>;
      return (
        <Empty inline title={`Sin receta para ${fuenteNombre}.`}>
          {c.etiqueta} no se puede pedir todavía: el resultado dirá «concepto no disponible».{" "}
          <Button ref={plantillaRef} variant="link" onClick={() => { setTextos((t) => ({ ...t, [claveReceta]: plantilla(c) })); foco.current = () => recetaRef.current?.focus(); }}>empezar una desde la plantilla</Button>.
        </Empty>
      );
    }
    const escenarioId = origen?.tipo === "fixture" ? origen.escenario : "";
    const ruta = tipo === "fixture" ? `content/escenarios/${escenarioId}/sources/` : base !== null ? (recetaRepo.data ? `content/connectors/${recetaRepo.data.fichero}` : rutaReceta("business-central", c.id)) : `${rutaReceta("business-central", c.id)} · aún no existe en el repo`;
    const ref = recetaRepo.data?.receta;
    return (
      <>
        <div className="sim-recipe-meta">
          <Sub inline>{ruta}</Sub>
          {tipo === "fixture" && <Badge>derivada del escenario</Badge>}
          {tipo === "business-central" && ref !== undefined && <Badge>{`v${ref.version} · ${ref.status}`}</Badge>}
          {(base === null || editada) && <Button variant="link" onClick={volverAlRepo}>{base === null ? "descartar la plantilla" : tipo === "fixture" ? "volver a la derivada" : "volver a la del repo"}</Button>}
        </div>
        <Field id="sim-receta" label={`Receta de ${c.etiqueta} en YAML`} hideLabel {...(errReceta !== null ? { error: errReceta } : {})}>
          <Textarea ref={recetaRef} mono id="sim-receta" rows={Math.min(24, textoVisible.split("\n").length + 1)} spellCheck={false} value={textoVisible}
            aria-describedby={`sim-receta-glosa${errReceta !== null ? " sim-receta-e" : ""}`} aria-invalid={errReceta !== null || undefined}
            onChange={(e) => ponerTexto(e.target.value)} />
        </Field>
        <FormActs>
          <Button size="s" variant="go" loading={busy} disabled={red} onClick={probarReceta}>Ejecutar sin guardar</Button>
          <Button size="s" variant="quiet" onClick={descargar}>Descargar .yaml</Button>
          <Button size="s" variant="quiet" onClick={copiar}>Copiar</Button>
          <Help inline id="sim-receta-glosa">Nada se guarda aquí: cuando funcione, súbela al repo por PR.</Help>
        </FormActs>
      </>
    );
  }

  /* ── la pantalla entera ── */

  // el vacío de la derecha no contradice a la conexión: si hay fuente en uso, dice qué falta elegir
  function vacioDerecha(): ReactNode {
    if (conFuenteSinOrigen && fuente === "fixture") return (
      <Empty glyph="✳" title={escenarios.isError ? "No llego a los escenarios del banco" : "El banco no tiene escenarios con fuentes"}>
        {escenarios.isError
          ? "El banco está en uso, pero su lista de escenarios no ha llegado: en cuanto llegue, elijo el primero. El aviso con «reintentar ahora» está en la card de conexión."
          : "El banco está en uso, pero ningún escenario sirve datos todavía. Deja el banco y conecta Business Central para trabajar contra la organización de demo."}
      </Empty>
    );
    if (conFuenteSinOrigen) return (
      <Empty glyph="✳" title="Elige entorno y empresa">
        {entorno !== "" && empresas !== null && empresas.empresas.length === 0
          ? `Business Central está conectado, pero ${entorno} no tiene empresas: prueba otro entorno en el bloque Escaneo.`
          : "Business Central está conectado: el origen es una empresa de un entorno. Elígela en el bloque Escaneo para ver qué conceptos sirve."}
      </Empty>
    );
    return (
      <Empty glyph="✳" title={bc.fase === "conectando" ? "Esperando el permiso de Microsoft" : "Conecta una fuente para empezar"}>
        {bc.fase === "conectando"
          ? "Acepta el permiso de solo lectura en la ventana de Microsoft con la cuenta de la organización de demo. En cuanto vuelva, escaneo entornos y empresas."
          : "El simulador hace las mismas peticiones que hará el asesor contra la organización de demo de Skein y te enseña lo que vuelve: la petición, la respuesta en bruto y el resultado normalizado. Empieza por Business Central: CRONUS ES trae datos conocidos. Sin conexión, el banco de escenarios sirve datos fijos."}
        {/* el sistema pone cada CTA del vacío en su línea, centrado. Este vacío
            se va al conectar o al usar el banco: el foco pasa ANTES al mando
            de la conexión, que sigue a la vista (como el prototipo) */}
        {cuenta.configurada
          ? <Button variant="primary" loading={bc.fase === "conectando"} aria-disabled={bc.fase === "conectando" || undefined} onClick={() => { if (bc.fase === "conectando") return; bcActRef.current?.focus(); void conectar(); }}>{bc.fase === "conectando" ? "Conectando…" : "Conectar con Microsoft"}</Button>
          : <Button variant="primary" aria-disabled="true" aria-describedby="sim-bc-porque" tip={AVISO_FALTA_CONFIG}>Conectar con Microsoft</Button>}
        <Button variant={cuenta.configurada ? "quiet" : "primary"} onClick={() => { bancoActRef.current?.focus(); usarBanco(); }}>Usar el banco de escenarios</Button>
      </Empty>
    );
  }
  const derecha = cargando || (sinOrigen && (escaneando || (fuente === "fixture" && escenarios.isPending)))
    ? (
      <section ref={derechaRef} className="sim-stack" aria-busy="true" aria-label="Preparando el simulador">
        <Card><Lineas n={2} /></Card>
        <Card><Lineas n={1} bloque tras={1} /></Card>
      </section>
    )
    : sinOrigen ? <section ref={derechaRef} aria-label={conFuenteSinOrigen ? "Sin origen elegido" : "Sin fuente conectada"}>{vacioDerecha()}</section>
    : (
      <div ref={(el) => { derechaRef.current = el; }} className="sim-stack">
        {consulta()}
        {resultado()}
        {receta()}
        <NoCuadra />
      </div>
    );

  return (
    <>
      <PageHead
        title="Simulador de conectores"
        meta={<>
          <Badge>sin asesor por medio</Badge>
          <Badge>organización de demo · solo lectura</Badge>
          <Badge tone="accent">nada se guarda</Badge>
          <Fainted className="sim-ph-note">las mismas peticiones que hará el asesor, y lo que vuelve</Fainted>
        </>}
        actions={<ButtonLink variant="quiet" href="/simulador/explorar">Explorar la fuente</ButtonLink>}
      />
      {red && (
        <Banner tone="danger" className="sim-banner-gap">
          <b>No llego al Studio.</b> Lo que ves puede estar desactualizado y no se puede conectar ni ejecutar nada. Reintento solo cada 30 s.{" "}
          <Button variant="link" onClick={() => void catalogo.refetch()}>reintentar ahora</Button>
        </Banner>
      )}
      <Stale stale={red} inert={red} className="sim-cols">
        <div ref={izquierdaRef} className="sim-stack">
          {conexion}
          {listaConceptos}
        </div>
        {derecha}
      </Stale>
      <LiveNote>{anuncio}</LiveNote>
    </>
  );
}
